import { Typography } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import type { CSSProperties } from "react";
import { useIntl } from "react-intl";
import { DataTableBooleanColumn } from "~components/DataTable/components/DataTableBooleanColumn/DataTableBooleanColumn.tsx";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { dateFormat, dateTimeSecondsFormat } from "~constants/dateFormat.ts";
import type { ConductTypes } from "~enums/conductTypes.ts";
import { ConductTableColumns } from "~pages/Conducts/ConductListPage/enums/conductTableColumns.ts";
import type { ConductTransformed } from "~pages/Conducts/types/conductTransformed.ts";
import type { Competition } from "~types/competition.ts";
import type { Person } from "~types/person.ts";
import type { Season } from "~types/season.ts";

const notesStyle: CSSProperties = { marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" };
const SORT_DIRECTIONS: SortOrder[] = ["descend", "ascend", "descend"];

export const useGetConductTableColumns = (): Array<DataTableColumn<ConductTransformed>> => {
  const { formatMessage } = useIntl();

  return [
    {
      key: ConductTableColumns.ConductId,
      title: formatMessage({ id: "conduct.id" }),
      dataIndex: ConductTableColumns.ConductId,
      copyable: true,
      width: 200,
    },
    {
      key: ConductTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: ConductTableColumns.ExternalId,
      copyable: true,
      width: 200,
    },
    {
      key: ConductTableColumns.Person,
      title: formatMessage({ id: "personname" }),
      dataIndex: ConductTableColumns.Person,
      render: (person: Person) => person?.nameFullLocal ?? "",
    },
    {
      key: ConductTableColumns.Role,
      title: formatMessage({ id: "person.role" }),
      dataIndex: ConductTableColumns.Role,
      width: 160,
      render: (role: string | null) => role ?? formatMessage({ id: "conduct.role.player" }),
    },
    {
      key: ConductTableColumns.Competition,
      title: formatMessage({ id: "competitionname" }),
      dataIndex: ConductTableColumns.Competition,
      width: 200,
      render: (competition: Competition) => competition?.nameLocal ?? "",
    },
    {
      key: ConductTableColumns.Season,
      title: formatMessage({ id: "seasonname" }),
      dataIndex: ConductTableColumns.Season,
      width: 200,
      render: (season: Season) => season?.nameLocal ?? "",
    },
    {
      key: ConductTableColumns.FixtureId,
      title: formatMessage({ id: "fixture.id" }),
      dataIndex: ConductTableColumns.FixtureId,
      copyable: true,
      width: 200,
    },
    {
      key: ConductTableColumns.ConductType,
      title: formatMessage({ id: "conduct.type" }),
      dataIndex: ConductTableColumns.ConductType,
      width: 140,
      render: (conductType: Array<ConductTypes>) => (
        <div>
          {(conductType || []).map((item) => (
            <div key={item}>{formatMessage({ id: `conductType.${item}` })}</div>
          ))}
        </div>
      ),
    },
    {
      key: ConductTableColumns.Added,
      title: formatMessage({ id: "date.added" }),
      dataIndex: ConductTableColumns.Added,
      width: 115,
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: ConductTableColumns.Updated,
      title: formatMessage({ id: "date.updated" }),
      dataIndex: ConductTableColumns.Updated,
      width: 115,
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeSecondsFormat} />,
    },
    {
      key: ConductTableColumns.Status,
      title: formatMessage({ id: "status" }),
      dataIndex: ConductTableColumns.Status,
      width: 100,
    },
    {
      key: ConductTableColumns.ConductNotes,
      title: formatMessage({ id: "conducts.table.column.conductNotes" }),
      dataIndex: ConductTableColumns.ConductNotes,
      width: 250,
      render: (conductNotes: string | null) =>
        conductNotes ? <Typography.Paragraph style={notesStyle}>{conductNotes}</Typography.Paragraph> : "",
    },
    {
      key: ConductTableColumns.DateOffenceLocal,
      title: formatMessage({ id: "conducts.table.column.dateOffenceLocal" }),
      dataIndex: ConductTableColumns.DateOffenceLocal,
      width: 130,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
    {
      key: ConductTableColumns.DateSuspendedFrom,
      title: formatMessage({ id: "conducts.table.column.dateSuspendedFrom" }),
      dataIndex: ConductTableColumns.DateSuspendedFrom,
      width: 130,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
    {
      key: ConductTableColumns.DateSuspendedTo,
      title: formatMessage({ id: "conducts.table.column.dateSuspendedTo" }),
      dataIndex: ConductTableColumns.DateSuspendedTo,
      width: 130,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
    {
      key: ConductTableColumns.LifeSentence,
      title: formatMessage({ id: "conducts.table.column.lifeSentence" }),
      dataIndex: ConductTableColumns.LifeSentence,
      width: 120,
      render: (value: boolean | null) => <DataTableBooleanColumn value={value} />,
    },
    {
      key: ConductTableColumns.FineAmount,
      title: formatMessage({ id: "conducts.table.column.fineAmount" }),
      dataIndex: ConductTableColumns.FineAmount,
      width: 100,
      align: "right",
      render: (value: number | null) => value ?? "",
    },
    {
      key: ConductTableColumns.FineCurrency,
      title: formatMessage({ id: "conducts.table.column.fineCurrency" }),
      dataIndex: ConductTableColumns.FineCurrency,
      width: 100,
    },
    {
      key: ConductTableColumns.FineStatus,
      title: formatMessage({ id: "conducts.table.column.fineStatus" }),
      dataIndex: ConductTableColumns.FineStatus,
      width: 110,
    },
    {
      key: ConductTableColumns.DateFinePaidLocal,
      title: formatMessage({ id: "conducts.table.column.dateFinePaidLocal" }),
      dataIndex: ConductTableColumns.DateFinePaidLocal,
      width: 130,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
  ];
};
